import { useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { TYPE_COLOR } from "@/lib/format";
import { GitBranch } from "lucide-react";

const TIERS = ["Epic", "Module", "Task", "Subtask", "Bug"];
const COL_WIDTH = 220;
const ROW_HEIGHT = 56;
const NODE_W = 180;
const NODE_H = 36;

const FAMILY_HEX = { violet: "#8B5CF6", green: "#22C55E", slate: "#64748B", red: "#EF4444", blue: "#2563EB", amber: "#F59E0B" };

export default function DependencyGraph() {
  const { project } = useOutletContext();
  const navigate = useNavigate();
  const { data: allItems = [], isLoading } = useQuery({ queryKey: ["items", project.id], queryFn: () => get(`/pm/projects/${project.id}/items`) });

  const withDeps = allItems.filter((i) => (i.blocked_by || []).length > 0);
  const relevantIds = new Set(withDeps.flatMap((i) => [i.id, ...i.blocked_by]));
  const nodes = allItems.filter((i) => relevantIds.has(i.id));

  const positions = useMemo(() => {
    const pos = {};
    TIERS.forEach((tier, colIdx) => {
      const inTier = nodes.filter((n) => n.type === tier);
      inTier.forEach((n, rowIdx) => {
        pos[n.id] = { x: colIdx * COL_WIDTH + 20, y: rowIdx * ROW_HEIGHT + 20 };
      });
    });
    return pos;
  }, [nodes]);

  const maxRows = Math.max(1, ...TIERS.map((t) => nodes.filter((n) => n.type === t).length));
  const width = TIERS.length * COL_WIDTH + 40;
  const height = maxRows * ROW_HEIGHT + 40;

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div>
      <PageHeader title="Dependencies" subtitle="Blocked-by relationships across this project's tickets." />
      {nodes.length === 0 ? <EmptyState icon={GitBranch} title="No dependencies to show" description="Add a dependency in a ticket's detail page to see it graphed here." /> : (
        <div className="overflow-auto rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex gap-4 text-xs text-muted-foreground">
            {TIERS.map((t) => <span key={t} className="w-[220px]">{t}</span>)}
          </div>
          <svg width={width} height={height}>
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#94A3B8" />
              </marker>
            </defs>
            {withDeps.flatMap((item) =>
              (item.blocked_by || []).filter((b) => positions[b] && positions[item.id]).map((b) => {
                const from = positions[b], to = positions[item.id];
                const x1 = from.x + NODE_W, y1 = from.y + NODE_H / 2;
                const x2 = to.x, y2 = to.y + NODE_H / 2;
                const midX = (x1 + x2) / 2;
                return (
                  <path key={`${b}-${item.id}`} d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`}
                    fill="none" stroke="#94A3B8" strokeWidth="1.5" markerEnd="url(#arrow)" />
                );
              })
            )}
            {nodes.map((n) => {
              const p = positions[n.id];
              if (!p) return null;
              return (
                <g key={n.id} transform={`translate(${p.x}, ${p.y})`} className="cursor-pointer" onClick={() => navigate(`/pm/projects/${project.id}/items/${n.id}`)}>
                  <rect width={NODE_W} height={NODE_H} rx="6" fill="hsl(var(--card))" stroke={FAMILY_HEX[TYPE_COLOR[n.type]] || "#94A3B8"} strokeWidth="1.5" />
                  <text x="10" y={NODE_H / 2 + 4} fontSize="11" fill="currentColor" className="fill-foreground">
                    {n.title.length > 22 ? n.title.slice(0, 22) + "…" : n.title}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}
